import { useEffect, useState } from 'react';

/**
 * @description A hook utility that will wrap a stateful value and produce a new value after a given delay
 *
 * @example
 * const [searchInput, setSearchInput] = useState('');
 * const debouncedSearchInput = useDebounce(searchInput, 500);
 * useEffect(() => {
 *  // debouncedSearchInput would have been changed in 500 milliseconds
    // based on the change in 'searchInput'
    doSomethingBasedOnDelayedSearchInput()
  }, [debouncedSearchInput]);
  // later in the code
  <button onClick={() => setSearchInput('something else')}>
 *
 *
 * @see https://usehooks-ts.com/react-hook/use-debounce
 *
 * @param {any} value value to be debounced
 * @param {any} delay number in milliseconds to delay by
 *
 */
function useDebounce<T>(value: T, delay?: number): T {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => setDebouncedValue(value), delay || 500);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export default useDebounce;
